import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Subheading from '../components/font/Subheading';
import Modal from '../components/overlay/Modal';
import JobHistory from '../components/shopify/JobHistory';
import { renderOptions } from '../components/form/selectOptions';
import { useToast } from '../components/toast/ToastProvider';
import { ShopifyDetailsResponse, ShopifyOwner, ShopifyWebhook } from '../models/shopify';
import {
  createShopifyWebhook, deleteShopifyWebhook, fixShopifyWebhooks, getShopifyDetails, updateShopifyWebhook,
} from '../requests/shopify.request';

const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '—';
const join = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(', ') || '—';

const errorMessage = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof message === 'string' ? message : fallback;
};

const OwnerDetails = ({ owner }: { owner: ShopifyOwner }) => {
  const plan = owner.plan && [
    owner.plan.publicDisplayName,
    owner.plan.shopifyPlus ? 'Plus' : null,
    owner.plan.partnerDevelopment ? 'Development store' : null,
  ].filter(Boolean).join(' · ');
  const rows: [string, React.ReactNode][] = [
    ['Owner', owner.shopOwnerName || '—'],
    ['Owner email', owner.email ? <a href={`mailto:${owner.email}`}>{owner.email}</a> : '—'],
    ['Contact email', owner.contactEmail || '—'],
    ['Phone', owner.billingAddress?.phone || '—'],
    ['Location', join(owner.billingAddress?.city, owner.billingAddress?.province, owner.billingAddress?.country)],
    ['Store name', owner.name],
    ['Primary domain', owner.primaryDomain?.host || '—'],
    ['myshopify domain', owner.myshopifyDomain],
    ['Plan', plan || '—'],
    ['Currency', owner.currencyCode || '—'],
    ['Timezone', owner.ianaTimezone || '—'],
    ['Created', formatDate(owner.createdAt)],
  ];
  return (
    <Details>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </Details>
  );
};

type WebhooksProps = {
  webhooks: ShopifyWebhook[];
  onEdit: (webhook: ShopifyWebhook) => void;
  onDelete: (webhook: ShopifyWebhook) => void;
};

const Webhooks = ({ webhooks, onEdit, onDelete }: WebhooksProps) => {
  if (!webhooks.length) return <Muted>No webhooks registered through the API.</Muted>;
  return (
    <s-table>
      <s-table-header-row>
        <s-table-header listSlot='primary'>Topic</s-table-header>
        <s-table-header listSlot='secondary'>Endpoint</s-table-header>
        <s-table-header listSlot='labeled'>Format</s-table-header>
        <s-table-header listSlot='labeled'>Filter / fields</s-table-header>
        <s-table-header listSlot='labeled'>Updated</s-table-header>
        <s-table-header listSlot='inline'><VisuallyHidden>Actions</VisuallyHidden></s-table-header>
      </s-table-header-row>
      <s-table-body>
        {webhooks.map(webhook => (
          <s-table-row key={webhook.id}>
            <s-table-cell><Code>{webhook.topic}</Code></s-table-cell>
            <s-table-cell><Endpoint>{webhook.uri}</Endpoint></s-table-cell>
            <s-table-cell>{webhook.format}</s-table-cell>
            <s-table-cell>{join(webhook.filter, webhook.includeFields?.join(' '))}</s-table-cell>
            <s-table-cell>{formatDate(webhook.updatedAt)}</s-table-cell>
            <s-table-cell>
              <RowActions>
                <s-button variant='tertiary' icon='edit' onClick={() => onEdit(webhook)}
                  accessibilityLabel={`Edit ${webhook.topic} webhook to ${webhook.uri}`} />
                <s-button variant='tertiary' tone='critical' icon='delete' onClick={() => onDelete(webhook)}
                  accessibilityLabel={`Delete ${webhook.topic} webhook to ${webhook.uri}`} />
              </RowActions>
            </s-table-cell>
          </s-table-row>
        ))}
      </s-table-body>
    </s-table>
  );
};

const Shopify = () => {
  const { id } = useParams();
  const toast = useToast();
  const [details, setDetails] = useState<ShopifyDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const [adding, setAdding] = useState(false);
  const [topic, setTopic] = useState('');
  const [address, setAddress] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ShopifyWebhook | null>(null);
  const [editAddress, setEditAddress] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShopifyWebhook | null>(null);
  const [fixOpen, setFixOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const current = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await getShopifyDetails(id);
      if (current === requestId.current) setDetails(response);
    } catch (loadError) {
      if (current === requestId.current) {
        setDetails(null);
        setError(errorMessage(loadError, 'Could not load Shopify data.'));
      }
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setDetails(null);
    load();
  }, [load]);

  const defaults = details?.default_webhooks ?? [];
  const webhooks = details?.webhooks.data;
  // A default webhook only counts as registered on its default address; the same topic elsewhere doesn't.
  const missing = webhooks ? defaults.filter(item => !webhooks.some(
    webhook => webhook.topic === item.topic && webhook.uri === item.uri,
  )) : [];
  // Fall back to the default topics if the schema lookup failed.
  const topics = details?.topics.data ?? defaults.map(item => item.topic);
  const defaultUri = (value: string) => defaults.find(item => item.topic === value)?.uri ?? '';

  const changeTopic = (value: string) => {
    // Suggest the default address for a default topic unless the address was edited.
    if (!address.trim() || address === defaultUri(topic)) setAddress(defaultUri(value));
    setTopic(value);
  };

  const openAdd = () => {
    const suggested = missing[0] ?? defaults[0];
    setTopic(suggested?.topic ?? topics[0] ?? '');
    setAddress(suggested?.uri ?? '');
    setAddError(null);
    setAdding(true);
  };

  const mutate = async (action: () => Promise<string | null>, onError: (message: string) => void, fallback: string) => {
    setSaving(true);
    try {
      const notice = await action();
      if (notice) toast.show(notice);
      return true;
    } catch (mutationError) {
      onError(errorMessage(mutationError, fallback));
      return false;
    } finally {
      setSaving(false);
      load();
    }
  };

  const add = async () => {
    if (!id || !topic) return;
    const added = await mutate(async () => {
      await createShopifyWebhook(id, topic, address.trim());
      return `Added ${topic} webhook`;
    }, setAddError, 'Could not add the webhook.');
    if (added) setAdding(false);
  };

  const openEdit = (webhook: ShopifyWebhook) => {
    setEditAddress(webhook.uri);
    setEditError(null);
    setEditTarget(webhook);
  };

  const edit = async () => {
    if (!id || !editTarget) return;
    const updated = await mutate(async () => {
      await updateShopifyWebhook(id, editTarget.id, editAddress.trim());
      return `Updated ${editTarget.topic} webhook`;
    }, setEditError, 'Could not update the webhook.');
    if (updated) setEditTarget(null);
  };
  const editDefault = editTarget ? defaultUri(editTarget.topic) : '';

  const remove = async () => {
    if (!id || !deleteTarget) return;
    await mutate(async () => {
      await deleteShopifyWebhook(id, deleteTarget.id);
      return `Deleted ${deleteTarget.topic} webhook`;
    }, message => toast.show(message, { isError: true }), 'Could not delete the webhook.');
    setDeleteTarget(null);
  };

  const fix = async () => {
    if (!id) return;
    await mutate(async () => {
      const result = await fixShopifyWebhooks(id);
      if (result.failed.length) {
        toast.show(`Could not register ${result.failed.map(item => `${item.topic} (${item.error})`).join(', ')}`,
          { isError: true, duration: 10000 });
      }
      return result.created.length ? `Registered ${result.created.join(', ')}` :
        result.failed.length ? null : 'All default webhooks were already registered';
    }, message => toast.show(message, { isError: true }), 'Could not fix the webhooks.');
    setFixOpen(false);
  };

  return (
    <Container>
      <Header>
        <s-heading fontSize='large'>Shopify</s-heading>
        <s-button icon='refresh' onClick={load} disabled={loading} accessibilityLabel='Reload Shopify data'>Reload</s-button>
      </Header>
      <Muted>Live data from the Shopify Admin GraphQL API{details ? ` (${details.api_version})` : ''}.</Muted>
      {error && <s-banner tone='critical'><p>{error}</p></s-banner>}
      {loading && !details && <Loading><s-spinner size='large' accessibilityLabel='Loading Shopify data' /></Loading>}
      {details && (
        <>
          <s-section>
            <SectionBody>
              <Subheading>Shop owner</Subheading>
              {details.owner.error && <s-banner tone='critical'><p>{details.owner.error}</p></s-banner>}
              {details.owner.data && <OwnerDetails owner={details.owner.data} />}
            </SectionBody>
          </s-section>
          <s-section>
            <SectionBody>
              <Header>
                <Subheading>{webhooks ? `Registered webhooks (${webhooks.length})` : 'Registered webhooks'}</Subheading>
                {webhooks && (
                  <Actions>
                    <s-button icon='plus' onClick={openAdd}>Add webhook</s-button>
                    <s-button variant='primary' onClick={() => setFixOpen(true)} disabled={!missing.length}>Fix webhooks</s-button>
                  </Actions>
                )}
              </Header>
              {details.webhooks.error && <s-banner tone='critical'><p>{details.webhooks.error}</p></s-banner>}
              {webhooks && (missing.length ? (
                <s-banner tone='warning'>
                  <p>Missing default webhooks: {missing.map(item => item.label).join(', ')}</p>
                </s-banner>
              ) : <Muted>All default webhooks are registered.</Muted>)}
              {webhooks && <Webhooks webhooks={webhooks} onEdit={openEdit} onDelete={setDeleteTarget} />}
              <Muted>Webhooks declared in the app's TOML configuration are not listed by this API.</Muted>
            </SectionBody>
          </s-section>
        </>
      )}
      {id && <JobHistory shopId={id} />}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        heading='Add webhook'
        primaryAction={{ content: 'Add webhook', onAction: add, loading: saving, disabled: !topic || !address.trim() }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setAdding(false) }]}
      >
        <s-stack gap='base'>
          <s-select label='Topic' value={topic} onChange={(event) => changeTopic(event.currentTarget.value)}>
            {renderOptions(topics.map(value => ({ label: value, value })))}
          </s-select>
          <s-url-field label='Address' value={address} placeholder={defaults[0]?.uri}
            details='The full https:// address Shopify sends events to.'
            onInput={(event) => setAddress(event.currentTarget.value)} />
          {addError && <s-banner tone='critical'><p>{addError}</p></s-banner>}
        </s-stack>
      </Modal>

      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        heading='Edit webhook'
        primaryAction={{
          content: 'Save', onAction: edit, loading: saving,
          disabled: !editAddress.trim() || editAddress.trim() === editTarget?.uri,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEditTarget(null) }]}
      >
        <s-stack gap='base'>
          <p>Topic: <Code>{editTarget?.topic}</Code></p>
          <s-url-field label='Address' value={editAddress}
            details="The full https:// address Shopify sends events to. Shopify doesn't allow changing the topic."
            onInput={(event) => setEditAddress(event.currentTarget.value)} />
          {editDefault && editAddress.trim() !== editDefault && (
            <div>
              <s-button variant='tertiary' onClick={() => setEditAddress(editDefault)}>Use default address</s-button>
            </div>
          )}
          {editError && <s-banner tone='critical'><p>{editError}</p></s-banner>}
          <Muted>This changes the live shop and can't be undone from the History page.</Muted>
        </s-stack>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        heading='Delete webhook?'
        primaryAction={{ content: 'Delete', destructive: true, loading: saving, onAction: remove }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setDeleteTarget(null) }]}
      >
        <p>
          Shopify will stop sending {deleteTarget?.topic} events to <Endpoint>{deleteTarget?.uri}</Endpoint>.
          This changes the live shop and can't be undone from the History page.
        </p>
      </Modal>

      <Modal
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        heading='Fix webhooks?'
        primaryAction={{ content: 'Register webhooks', loading: saving, onAction: fix }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setFixOpen(false) }]}
      >
        <s-stack gap='base'>
          <p>These webhooks will be registered:</p>
          <FixList>
            {missing.map(item => <li key={item.topic}><Code>{item.label}</Code> → <Endpoint>{item.uri}</Endpoint></li>)}
          </FixList>
          <Muted>Existing webhooks are left as they are.</Muted>
        </s-stack>
      </Modal>
    </Container>
  );
};

export default Shopify;

const Container = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RowActions = styled.div`
  display: flex;
  gap: 4px;
`;

const SectionBody = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const Muted = styled.p`
  margin: 0;
  color: #6d7175;
`;

const Details = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 8px 24px;
  margin: 0;
  dt { color: #6d7175; }
  dd { margin: 0; overflow-wrap: anywhere; }
`;

const FixList = styled.ul`
  display: grid;
  gap: 4px;
  margin: 0;
  padding-left: 20px;
`;

const Code = styled.code`
  font-size: 12px;
`;

const Endpoint = styled.span`
  overflow-wrap: anywhere;
  font-family: monospace;
  font-size: 12px;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
